/**
 * Shared helpers for Intake & Demand Management — scoring criteria, weighted
 * priority score, stage labels, and defaults. Used by the Intake page and the
 * demand store so the math and labels stay in one place.
 */

import type { Demand, DemandStage, DemandSettings } from '@/types';

export const DEMAND_STAGE_LABELS: Record<DemandStage, string> = {
  new: 'New',
  under_review: 'Under review',
  approved: 'Approved',
  rejected: 'Rejected',
  deferred: 'Deferred',
  converted: 'Converted',
};

export const DEMAND_STAGES = Object.keys(DEMAND_STAGE_LABELS) as DemandStage[];

/** Stages still "in the pipeline" (awaiting a decision). */
export const OPEN_DEMAND_STAGES: DemandStage[] = ['new', 'under_review'];

export type DemandWeights = Omit<DemandSettings, 'organizationId'>;

export const DEFAULT_DEMAND_WEIGHTS: DemandWeights = {
  weightAlignment: 30,
  weightValue: 30,
  weightUrgency: 15,
  weightRisk: 15,
  weightEffort: 10,
};

export interface ScoringCriterion {
  key: string;
  label: string;
  hint: string;
  scoreField: 'scoreAlignment' | 'scoreValue' | 'scoreUrgency' | 'scoreRisk' | 'scoreEffort';
  weightField: keyof DemandWeights;
}

export const SCORING_CRITERIA: ScoringCriterion[] = [
  { key: 'alignment', label: 'Strategic alignment', hint: '5 = strongly aligned', scoreField: 'scoreAlignment', weightField: 'weightAlignment' },
  { key: 'value', label: 'Business value / ROI', hint: '5 = high value', scoreField: 'scoreValue', weightField: 'weightValue' },
  { key: 'urgency', label: 'Urgency', hint: '5 = very urgent', scoreField: 'scoreUrgency', weightField: 'weightUrgency' },
  { key: 'risk', label: 'Risk', hint: '5 = low risk', scoreField: 'scoreRisk', weightField: 'weightRisk' },
  { key: 'effort', label: 'Effort / cost', hint: '5 = low effort', scoreField: 'scoreEffort', weightField: 'weightEffort' },
];

/**
 * Weighted priority score on a 0–5 scale. Only counts criteria that have been
 * scored (so a partially-scored demand still gets a meaningful score), weighting
 * by the workspace's configured weights. Returns null when nothing is scored.
 */
export function computeDemandScore(d: Demand, w: DemandWeights): number | null {
  let weighted = 0;
  let totalWeight = 0;
  for (const c of SCORING_CRITERIA) {
    const s = d[c.scoreField];
    if (typeof s === 'number') {
      weighted += s * w[c.weightField];
      totalWeight += w[c.weightField];
    }
  }
  if (totalWeight === 0) return null;
  return weighted / totalWeight;
}

export function isDemandScored(d: Demand): boolean {
  return SCORING_CRITERIA.some((c) => typeof d[c.scoreField] === 'number');
}

/** All five criteria scored — required before a demand can be approved. */
export function isDemandFullyScored(d: Demand): boolean {
  return SCORING_CRITERIA.every((c) => typeof d[c.scoreField] === 'number');
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** A deferred demand whose revisit date has arrived/passed. */
export function isDemandReviewDue(d: Demand, today: string = todayIso()): boolean {
  return d.stage === 'deferred' && !!d.revisitDate && d.revisitDate <= today;
}
